import { SxProp, View } from 'dripsy';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SkeletonView } from '@/design-system/components/cards';
import { Icon } from '@/design-system/components/icons/Icon';
import { TextBody, TextCaption } from '@/design-system/components/typography';
import { UserEarnOfferListResponseDataItem } from '@utx/utx-client';
import { AllOffersWebList } from './AllOffersWebList';
import { AllOffersWebTitle } from './AllOffersWebTitle';
import { NoAnimationTabView } from '@/design-system/components/tabs/NoAnimationTabView';
import { OffersByCategory } from '../../hooks/useOffers.hook';

interface AllOffersProps {
  offers: Array<UserEarnOfferListResponseDataItem>;
  offersByCategory: OffersByCategory;
  onSearch: (value?: string) => void;
  sx?: SxProp;
  loading?: boolean;
  showAssets: {
    showMyAssetsOnly: boolean;
    toggleShowMyAssetsOnly: () => void;
  };
}

export const AllOffersWeb: React.FC<AllOffersProps> = ({ offers, offersByCategory, sx, loading, onSearch, showAssets }) => {
  const { t } = useTranslation();

  const tabs = Object.keys(offersByCategory)
    .sort((a, b) => {
      // sort by name alphabetically
      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }
      return 0;
    })
    .map((category) => {
      return {
        name: t(`earn-invest::glossary.${category.split(' ').join('-').toLowerCase()}`),
        title: t(`earn-invest::glossary.${category.split(' ').join('-').toLowerCase()}`),
        component: AllOffersWebList,
        children: <AllOffersWebList loading={loading} offers={offersByCategory[category]} />,
        data: offersByCategory[category]
      };
    });

  return (
    <View sx={{ ...styles.container, ...sx }}>
      <AllOffersWebTitle onSearch={onSearch} showAssets={showAssets} />
      {/* Tab */}
      {loading && (
        <View sx={{ ...styles.container, gap: '$3' }}>
          <SkeletonView height={20} />
          <SkeletonView height={20} width={200} />
          <SkeletonView height={20} width={150} />
        </View>
      )}
      {!loading && (!offersByCategory || Object.keys(offersByCategory).length === 0) && (
        <View sx={{ paddingY: '$20', justifyContent: 'center', alignItems: 'center', gap: '$2' }}>
          <Icon name="listZeroState" height={60} width={60} />
          <TextBody bold label>
            {t('earn-invest::glossary.no-offers')}
          </TextBody>
        </View>
      )}
      {!loading && Object.keys(offersByCategory).length > 0 && <NoAnimationTabView tabs={tabs} data={tabs.map((tab) => tab.data)} />}
      {/* Alert */}
      <TextCaption
        secondary
        sx={{
          p: '$2'
        }}>
        {t('earn-invest::glossary.investment-advice-disclaimer', { vendor: 'UTGL' })}
      </TextCaption>
    </View>
  );
};

enum StyleName {
  container = 'container'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    overflow: 'hidden'
  }
};
